import { allNodesOfType } from 'components/utility/TreeHelpers'
import { INode } from 'shared/types/tree'
import { NodeType } from 'shared/types/ingestion'
import { deleteNode } from '../TreeActions'
import { GDriveSpreadsheet } from '../googleApis/types'
import { doInBatches } from 'shared/utils'
import {
  appendNodeToTree,
  NodeRequestParams,
  retrieveIds,
} from '../TreeRequests'

export function deleteSectionOverviews(tree: INode) {
  const sectionLevelPlanningGuides = allNodesOfType(
    tree,
    NodeType.LessonPlan,
  ).filter((s) => s.title === 'Sections Overview')
  const delObjs = sectionLevelPlanningGuides.map((s) => {
    return {
      treeId: s.tree_id,
      versionId: s.version,
      nodeId: s.id,
    }
  })
  return doInBatches(delObjs, deleteNode)
}

export function addSectionOverviews(
  tree: INode,
  overviews: Record<number, sectionOverview[]>,
) {
  const unitNumbersToUnitNodes = mapUnitNumbersToUnitNodes(tree)
  const overviewObjs = loopThroughUnits(
    overviews,
    unitNumbersToUnitNodes,
    getSectionOverviewForUnit,
  )
  return doInBatches(overviewObjs.returnValue, appendNodeToTree)
}

export function mapUnitNumbersToUnitNodes(tree: INode) {
  const treeUnits = allNodesOfType(tree, NodeType.Unit)
  return treeUnits.reduce(
    (unitNumberToUnitNodeMap: Record<number, INode>, tu) => {
      const unitNumber = parseInt(tu.title.split(':')[0].split(' ')[1])
      unitNumberToUnitNodeMap[unitNumber] = tu
      return unitNumberToUnitNodeMap
    },
    {},
  )
}

export function loopThroughUnits<T>(
  unitsToSectionOverviews: Record<number, sectionOverview[]>,
  unitNodes: Record<number, INode>,
  callback: (unitNode: INode, sectionOverview: sectionOverview[]) => T,
) {
  const missingUnits: number[] = []
  const returnValue = Object.keys(unitsToSectionOverviews).reduce<T[]>(
    (acc, unitNumberString) => {
      const unitNumber = parseInt(unitNumberString)
      const unitNode = unitNodes[unitNumber]
      if (!unitNode) {
        missingUnits.push(unitNumber)
        return acc
      }
      acc.push(callback(unitNode, unitsToSectionOverviews[unitNumber]))
      return acc
    },
    [],
  )
  return { missingUnits, returnValue }
}

export function getSectionOverviewForUnit(
  unitNode: INode,
  sectionOverview: sectionOverview[],
): NodeRequestParams {
  const unitOverviewIndex = unitNode.nodes!.findIndex(
    (n) => n.title.toUpperCase().trim() === 'UNIT NARRATIVE',
  )

  const newLesson = {
    type: NodeType.LessonPlan,
    title: 'Sections Overview',
    nodes: sectionOverview.map((so) => {
      return {
        type: NodeType.Note,
        title: so.section_title,
        body: {
          value: so.section_overview.replace('\n\n', '<p><br></p>'),
        },
      }
    }),
  }
  const newLessonList = {
    type: NodeType.NodeList,
    title: 'Sections Overview',
    body: {
      type: NodeType.LessonPlan,
    },
    nodes: [newLesson],
  }

  return {
    ...retrieveIds(unitNode),
    ...newLessonList,
    next_sibling_node_id: unitNode.nodes![unitOverviewIndex + 1].id,
  }
}

const requiredHeadersList = ['UNIT_ID', 'SECTION_TITLE', 'SECTION_OVERVIEW']

export function parseSpreadsheet(spreadsheet: GDriveSpreadsheet) {
  const gradeToUnitsToSectionOverviews: Record<
    string,
    Record<number, sectionOverview[]>
  > = {}
  spreadsheet.sheets.forEach((sheet) => {
    const headerIndices = sheet.getIndicesOfStringsInRow(0, requiredHeadersList)
    if (Object.values(headerIndices).includes(-1)) {
      console.error('Missing Headers')
      return
    }
    const unitsToSectionOverviews: Record<number, sectionOverview[]> = {}
    for (let i = 1; i < sheet.getRows().length; i++) {
      const row = sheet.getRowValues(i)
      const unitNumber = row[headerIndices['UNIT_ID']]
        ? parseInt(row[headerIndices['UNIT_ID']]!)
        : null
      if (!unitNumber) continue
      if (!unitsToSectionOverviews[unitNumber])
        unitsToSectionOverviews[unitNumber] = []
      unitsToSectionOverviews[unitNumber].push({
        section_title: row[headerIndices['SECTION_TITLE']]!,
        section_overview: row[headerIndices['SECTION_OVERVIEW']]!,
      })
      gradeToUnitsToSectionOverviews[sheet.properties.title] =
        unitsToSectionOverviews
    }
  })

  return gradeToUnitsToSectionOverviews
}

export type sectionOverview = {
  section_overview: string
  section_title: string
}
